import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import style from "./style.scss";
import { useIntl } from "react-intl";
import { Clickable } from "../../button/Clickable";
import { Icon } from "../../graphic/Icon";

export const Input = ({
    focusOn,
    error,
    maxLength = 255,
    onChange,
    onPrependClick,
    onAppendClick,
    big,
    icon,
    prepend,
    append,
    emphasize,
    className,
    id,
    name,
    placeholder,
    inputClassName,
    disabled,
    max,
    min,
    step,
    value = "",
    type = "text",
    onClick,
    onBlur,
    onEnter,
    autocomplete,
    allowedChars
}) => {
    const inputRef = useRef();
    const intl = useIntl();

    useEffect(() => {
        if (focusOn) {
            focusInput();
        }
    }, [focusOn]);

    useEffect(() => {
        if (error) {
            focusInput();
        }
    }, [error]);

    const handleChange = (e) => {
        let value = e.target.value;

        // Check id exceeding max length
        if (maxLength && value.length > maxLength) {
            value = value.slice(0, maxLength);
        }

        // Check allowed characters
        if (allowedChars) {
            const regex = new RegExp(`^[${allowedChars}]*$`);
            if (!regex.test(value)) {
                return;
            }
        }

        // Callback
        onChange && onChange(value);
    };

    /**
     * Set focus to input
     * @private
     */
    const focusInput = () => {
        setTimeout(() => {
            inputRef.current.focus();
        }, 50);
    };

    /**
     * Handle key press
     * @private
     */
    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            onEnter && onEnter(e);
        }
    };

    return (
        <div
            className={classNames(
                style.base,
                {
                    [style.big]: big,
                    [style.error]: error,
                    [style.inputIcon]: icon,
                    [style.inputPrepend]: prepend,
                    [style.inputAppend]: append,
                    [style.emphasize]: emphasize
                },
                className
            )}
        >
            {/* ICON */}
            {icon ? (
                <div className={style.icon}>
                    <Icon name={icon} />
                </div>
            ) : null}

            {/* PREPEND */}
            {prepend ? (
                <Clickable
                    className={classNames(style.prepend, {
                        [style.clickable]: !disabled && onPrependClick,
                        [style.disabled]: disabled
                    })}
                    onClick={!disabled && onPrependClick}
                >
                    <div className={style.content}>{prepend}</div>
                </Clickable>
            ) : null}

            {/* INPUT */}
            <input
                id={id}
                name={name}
                className={classNames(style.input, inputClassName)}
                placeholder={
                    placeholder && typeof placeholder === "object"
                        ? intl.formatMessage({ id: placeholder.props.id })
                        : placeholder
                }
                autoComplete={autocomplete}
                disabled={disabled}
                ref={inputRef}
                maxLength={maxLength}
                max={max}
                min={min}
                step={step}
                onChange={handleChange}
                onClick={onClick}
                onBlur={onBlur}
                onKeyPress={handleKeyPress}
                type={type}
                value={value == null || typeof value === "undefined" ? "" : value}
            />

            {/* APPEND */}
            {append ? (
                <Clickable
                    className={classNames(style.append, {
                        [style.clickable]: !disabled && onAppendClick,
                        [style.disabled]: disabled
                    })}
                    onClick={!disabled && onAppendClick}
                >
                    <div className={style.content}>{append}</div>
                </Clickable>
            ) : null}
        </div>
    );
};

Input.propTypes = {
    big: PropTypes.bool, // Render big input
    placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]), // Input placeholder
    onChange: PropTypes.func, // On value change callback
    type: PropTypes.string, // Input type (text, password, tel etc.)
    maxLength: PropTypes.number, // Maximum allowed characters
    allowedChars: PropTypes.string, // Allowed characters
    max: PropTypes.number, // Max value if number type
    min: PropTypes.number, // Min value if number type
    step: PropTypes.number, // Step
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]), // Render input error indication
    icon: PropTypes.string, // Show a prepended icon
    prepend: PropTypes.any, // Prepend icon or content
    append: PropTypes.any, // Append icon or content
    onPrependClick: PropTypes.func // Callback for when prepend is clicked
};
